import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LuShieldCheck, LuUsers, LuClipboardList } from 'react-icons/lu';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');

// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];

  const script = tokens.map(token=>token[1]);

  // Estimate speech and pauses separately when measured words are unavailable.

  // Paragraph breaks give each narrated detail time to remain highlighted.

  const speechSeconds = .4;

  const pauses = tokens.map(([_,word,space])=>

    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));

  const weights = pauses.map(pause=>speechSeconds+pause);

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);

    const scale = totalWeight ? (end-start)/totalWeight : 0;

    let cursor = start;

    for (let k = from; k < index; k += 1) {

      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};

      cursor += weights[k]*scale;

    }

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}

function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}

// Keep the same timing-input aliases and stable timeline as What Is Tribe.

function useNarrationTimeline(narration, wordTimings, duration) {

  const timingKey = JSON.stringify(Array.isArray(wordTimings)

    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);

  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);

}

// Read the audio clock each frame. Typing adds character boundaries so it

// stays in sync with speech, pause, seeking, and playback rate changes.

function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    const playbackChanged = controller.current.playbackId !== playbackId;

    if (!media) {

      const stamp = performance.now();

      const anchor = parentAnchor.current;

      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null

        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);

      // Anchor pause/resume to the elapsed clock instead of resetting to zero.

      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};

    }

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    if (controller.current.playbackId !== playbackId) {

      controller.current.playbackId = playbackId;

      controller.current.syncId += 1;

    }

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}

// Emphasis is presentation-only. Match whole words without depending on

// capitalization or punctuation; an absent emphasis phrase is simply skipped.

function emphasisIndexes(words, emphasis) {

  const source = words.map(normalise);

  const strong = new Set();

  for (const phrase of Array.isArray(emphasis) ? emphasis : []) {

    if (typeof phrase !== 'string') continue;

    const target = phrase.split(/\s+/).filter(Boolean).map(normalise).filter(Boolean);

    if (!target.length) continue;

    const offset = source.findIndex((_, index) => target.every((word, i) => source[index + i] === word));

    if (offset < 0) continue;

    target.forEach((_, index) => strong.add(offset + index));

  }

  return strong;

}



// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay

// fixed between clock updates, so CSS completes the gentle rise smoothly.

function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {

  const words = text.split(/\s+/).filter(Boolean);

  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);

  const strong = emphasisIndexes(words, emphasis);

  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>

    <span className={`safety-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{

      animationDelay: `${complete || settled ? -10 : (delay + index * .09) / clock.rate}s`,

      animationDuration: `${.38 / clock.rate}s`,

      animationPlayState: complete || clock.playing ? 'running' : 'paused',

    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

  </React.Fragment>)}</Tag>;

}









const SAFETY_POINTS = [

  { cue: 'If someone is being harassed', icon: LuShieldCheck, title: 'Intervene safely', description: 'Support the person affected.' },

  { cue: 'If a guest makes the space unsafe', icon: LuUsers, title: 'Get help', description: 'Involve venue staff or security when needed.' },

  { cue: 'Report the incident', icon: LuClipboardList, title: 'Report it', description: 'Tell the Tribe team as soon as possible.' },

];



function buildSafetyCues(narration, timeline) {

  const points = SAFETY_POINTS.map(point => ({ ...point, start: cueTime(narration, timeline, point.cue) }));

  return { points, listAt: points[0].start, reviewAt: cueTime(narration, timeline, 'Safety is always your number one priority') };

}



function SafetyPoints({ cues, clock, complete }) {

  const latest = complete ? cues.points.length - 1 : cues.points.reduce((index, point, i) => clock.now >= point.start ? i : index, -1);

  const review = complete || clock.now >= cues.reviewAt;

  return <ol className={`safety-field-items ${review ? 'is-reviewing' : ''}`} aria-label="Your safety priorities">

    {cues.points.map((point, index) => {

      const revealed = review || index <= latest;

      const current = !review && index === latest;

      const Icon = point.icon;

      return <li key={point.cue} className={`safety-field-item ${revealed ? 'is-revealed' : ''} ${current ? 'is-current' : ''}`} aria-hidden={revealed ? undefined : true} aria-current={current ? 'step' : undefined}>

        <span className="safety-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>

        <Icon className="safety-point-icon" size={30} strokeWidth={1.5} aria-hidden="true" focusable="false" />

        {revealed && <div className="safety-point-copy" key={`${point.cue}-${clock.syncId}`}>

          <PhraseReveal as="h2" text={point.title} clock={clock} complete={complete} />

          <PhraseReveal as="p" text={point.description} clock={clock} complete={complete} delay={.12} />

        </div>}

      </li>;

    })}

  </ol>;

}



// This lesson owns its visuals, narration metadata, and practice check.

export default function Safety(props = {}) {

  const narration = Safety.parts[0].narration;

  const speechTimeline = useNarrationTimeline(narration, props.wordTimings ?? props.words ?? EMPTY_TIMINGS, props.duration ?? 0);

  const cues = useMemo(() => buildSafetyCues(narration, speechTimeline), [narration, speechTimeline]);

  const timeline = useMemo(() => ({ ...speechTimeline, animationBoundaries: [cues.reviewAt] }), [speechTimeline, cues]);

  const clock = useLessonClock({ ...props, playbackId: `${props.playbackId ?? 0}:safety` }, timeline);

  const complete = props.audioStatus === 'Complete' || Boolean((props.audioElement ?? props.audioRef?.current)?.ended);

  const listVisible = complete || clock.now >= cues.listAt;

  return <div className="host-background-slide host-safety-slide" aria-label={Safety.title}>

    <style>{SAFETY_STYLES}</style>

    <section className={`safety-scene ${listVisible ? 'is-list' : 'is-caption'}`} data-scene={listVisible ? 'priorities' : 'intro'} data-timing-mode={timeline.mode}>

      <div className="safety-content">

        <div className="safety-headline" key={`headline-${clock.syncId}`}>

          <PhraseReveal text="Safety comes first." emphasis={['Safety']} clock={clock} complete={complete} />

        </div>

        {listVisible && <SafetyPoints cues={cues} clock={clock} complete={complete} />}

      </div>

    </section>

  </div>;

}



const SAFETY_STYLES = `

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');

.host-safety-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}

.host-safety-slide * {box-sizing:border-box;}

.host-safety-slide .safety-scene {min-height:530px;padding:36px 48px 46px;}

.host-safety-slide .safety-scene.is-caption {display:flex;align-items:center;justify-content:center;}

.host-safety-slide .safety-content {width:100%;max-width:1040px;margin:0 auto;}

.host-safety-slide .safety-headline {max-width:850px;margin:0 auto;text-align:center;}

.host-safety-slide .safety-headline h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(24px,2.55vw,32px);font-weight:700;line-height:1.15;letter-spacing:-.015em;}

.host-safety-slide .is-caption .safety-headline h1 {font-size:clamp(32px,4vw,50px);line-height:1.12;}

.host-safety-slide .safety-field-items {display:grid;grid-template-columns:repeat(3,minmax(0,1fr));list-style:none;width:100%;margin:80px auto 0;padding:0;}

.host-safety-slide .safety-field-item {min-width:0;min-height:215px;padding:0 24px;color:#111;border-left:1px solid #dedede;opacity:0;transition:opacity 420ms ease;}

.host-safety-slide .safety-field-item:first-child {border-left:0;padding-left:0;}

.host-safety-slide .safety-field-item:last-child {padding-right:0;}

.host-safety-slide .safety-field-item.is-revealed {opacity:.3;}

.host-safety-slide .safety-field-item.is-current,.host-safety-slide .is-reviewing .safety-field-item {opacity:1;}

.host-safety-slide .safety-number {display:grid;place-items:center;width:26px;height:26px;border:1px solid #d8d8d8;border-radius:50%;font-size:11px;font-weight:500;line-height:1;font-variant-numeric:tabular-nums;}

.host-safety-slide .safety-point-icon {display:block;margin:16px 0 0;color:currentColor;}

.host-safety-slide .safety-field-item h2 {margin:14px 0 12px;font-size:clamp(18px,1.7vw,21px);font-weight:650;line-height:1.3;letter-spacing:-.022em;}

.host-safety-slide .safety-field-item p {margin:0;max-width:280px;color:currentColor;font-size:clamp(13px,1.15vw,15px);font-weight:400;line-height:1.55;letter-spacing:-.014em;}

.host-safety-slide .safety-reveal-word {display:inline-block;opacity:0;transform:translateY(.36em);animation:safety-word-in 380ms cubic-bezier(.18,.84,.22,1) both;}

.host-safety-slide .safety-reveal-word.is-emphasized {font-weight:900;}

@keyframes safety-word-in {to{opacity:1;transform:translateY(0);}}

.host-training .ht-lesson-layout:has(.host-safety-slide) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.host-safety-slide)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.host-safety-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:700px){

 .host-safety-slide .safety-scene {min-height:540px;padding:28px 24px 38px;}

 .host-safety-slide .safety-headline h1 {font-size:clamp(23px,5.8vw,29px);}

 .host-safety-slide .is-caption .safety-headline h1 {font-size:clamp(28px,7.6vw,38px);}

 .host-safety-slide .safety-field-items {grid-template-columns:1fr;margin-top:40px;}

 .host-safety-slide .safety-field-item,.host-safety-slide .safety-field-item:first-child,.host-safety-slide .safety-field-item:last-child {min-height:0;padding:20px 0;border-left:0;border-top:1px solid #dedede;}

 .host-safety-slide .safety-field-item:first-child {border-top:0;}

 .host-safety-slide .safety-field-item h2 {margin:14px 0 8px;font-size:22px;}

 .host-safety-slide .safety-field-item p {max-width:100%;font-size:14px;}

}

@media(prefers-reduced-motion:reduce){

 .host-safety-slide .safety-reveal-word {animation:none!important;opacity:1;transform:none;}

 .host-safety-slide .safety-field-item {transition:none;}

}

`;



Object.assign(Safety, {

  "slide": 11,

  "id": "safety",

  "title": "Safety Comes First",

  "section": "Facilitation",

  "parts": [

    {

      "part": 0,

      "narration": "Lastly, when facilitating an event, your first priority is safety.\n\nIf someone is being harassed, physically or verbally, intervene safely and support the person affected.\n\nIf a guest makes the space unsafe, get help. Involve venue staff or security when needed, and ask the guest to leave when it is safe to do so.\n\nReport the incident to the Tribe team as soon as possible.\n\nSafety is always your number one priority."

    }

  ],

  "summary": [

    "Support the person affected.",

    "Get help when needed. Report the incident."

  ],

  "check": {

    "prompt": "A guest is being harassed. What comes first?",

    "choices": [

      "Keeping the event on schedule.",

      "Avoiding an uncomfortable conversation.",

      "Intervening safely and supporting the person affected."

    ],

    "correct": 2,

    "explanation": "Safety is your priority. Involve staff or security when needed and report the incident to Tribe."

  }

});
